import React, {
  createContext,
  useContext,
  useState,
  useRef,
  ReactNode,
  useMemo,
  useCallback,
} from "react";

import KeycloakType, { KeycloakProfile } from "keycloak-js";
import { AccessRole } from "../enum/UserAccessRole";
import Sentry, { setTagInSentry, setUserInSentry } from "../libs/sentry";
import { Analytics } from "libs/analytics";
import KeycloakService from "libs/keycloak";

const KeycloakContext = createContext<{
  keycloakInstance: KeycloakType | undefined;
  authenticated: boolean;
  profile?: KeycloakProfile;
  roles: AccessRole[];
  logout: () => void;
  initializeKeycloak: () => void;
  hasRoles: (role: AccessRole[]) => boolean;
  isUserMusa: () => boolean;
  isHaulerAdmin: () => boolean;
}>({
  keycloakInstance: undefined,
  authenticated: false,
  roles: [],
  logout: () => {},
  initializeKeycloak: () => {},
  hasRoles: () => false,
  isUserMusa: () => false,
  isHaulerAdmin: () => false,
});

export const KeycloakProvider = ({ children }: { children: ReactNode }) => {
  const keycloakInitialized = useRef(false);

  const [roles, setRoles] = useState<AccessRole[]>([]);
  const [profile, setProfile] = useState<KeycloakProfile>();
  const [authenticated, setAuthenticated] = useState(false);
  const [keycloakInstance, setKeycloakInstance] = useState<KeycloakType>();

  const initializeKeycloak = async () => {
    if (!keycloakInitialized.current) {
      keycloakInitialized.current = true;

      const _keycloak = await KeycloakService.getInstance();

      _keycloak
        .init({ onLoad: "check-sso" })
        .then((auth) => {
          setAuthenticated(auth);
          setKeycloakInstance(_keycloak);
          if (!auth) {
            _keycloak.login({
              redirectUri: window.location.href,
            });
            localStorage.clear();
          }

          if (auth) {
            _keycloak.loadUserProfile().then((profile) => {
              if (
                profile?.email &&
                !profile?.email.endsWith("@musa.co") &&
                !profile.username.endsWith("@musa.co")
              ) {
                Analytics.init();
                Analytics.setUserId(profile.id);
                Analytics.setUserProperties({
                  email: profile.email,
                  username: profile.username,
                });
              }

              setUserInSentry({
                id: profile.id,
                email: profile?.email,
                username: profile.username,
              });
              setTagInSentry(
                "name",
                profile?.firstName + " " + profile?.lastName
              );
              setProfile(profile);

              const token = _keycloak.tokenParsed;
              const realmRoles = token?.realm_access?.roles || [];
              const clientRoles = Object.values(
                token?.resource_access || {}
              ).flatMap((resource: any) => resource.roles || []);
              setRoles([...realmRoles, ...clientRoles]);
            });
          }
        })
        .catch((error) => {
          console.log(error);
          Sentry.captureException(error);
          setAuthenticated(false);
        });
    }
  };

  const hasRoles = useCallback(
    (role: AccessRole[]) => {
      return roles.some((userRole) => role.includes(userRole));
    },
    [roles]
  );

  const isUserMusa = useCallback(() => {
    if (!profile?.username) {
      return false;
    }
    return profile?.username.search(/@musa.co/i) !== -1;
  }, [profile?.username]);

  const isHaulerAdmin = useCallback(() => {
    return hasRoles([AccessRole.HAULER_ADMIN]);
  }, [hasRoles]);

  const logout = useCallback(() => {
    keycloakInstance.logout();
    localStorage.clear();
  }, [keycloakInstance]);

  const contextValue = useMemo(
    () => ({
      keycloakInstance,
      authenticated,
      profile,
      roles,
      logout,
      hasRoles,
      initializeKeycloak,
      isUserMusa,
      isHaulerAdmin,
    }),
    [
      keycloakInstance,
      authenticated,
      profile,
      roles,
      hasRoles,
      isUserMusa,
      isHaulerAdmin,
      logout,
    ]
  );

  return (
    <KeycloakContext.Provider value={contextValue}>
      {children}
    </KeycloakContext.Provider>
  );
};

export const useKeycloak = () => {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error("useKeycloak must be used within a KeycloakProvider");
  }
  return context;
};
